import { useEffect, useRef, useState } from "react";
import Cropper, {
  getInitialCropFromCroppedAreaPercentages,
  type Area,
  type MediaSize,
  type Point,
  type Size,
} from "react-easy-crop";
import {
  AREA_UTIL,
  enquadramentoInicial,
  type AreaNaCelula,
  type Enquadramento,
  type Tamanho,
} from "@foto/shared";
import { Botao, Girando } from "../../components/ui.js";

/** Medida em milímetros como se escreve em português: "93,6", não "93.6". */
export const emMm = (mm: number): string => String(mm).replace(".", ",");

/** Quatro casas bastam: a diferença cabe em menos de um pixel da folha. */
const arredondar = (n: number) => Math.round(n * 10_000) / 10_000;

/** O que o editor precisa da página para os botões de IA. */
export interface IaDoEditor {
  ocupado: "ajustar" | "ampliar" | null;
  /** Fotos que o pacote ainda comporta: nulo = sem limite; indefinido = carregando. */
  restantes: number | null | undefined;
  /** A célula já usa uma ampliação de cenário. */
  ampliada: boolean;
  /** O resultado da última chamada, ou o erro, em português. */
  mensagem: string | null;
  aoAjustar(): void;
  aoAmpliar(): void;
  aoVoltarAOriginal(): void;
}

/**
 * O operador escolhe o que da foto aparece na célula: arrasta para
 * reposicionar, dá zoom (pinça no celular, roda no computador, ou a barra) e
 * pode afastar a foto até sobrar margem branca.
 *
 * O gesto é do `react-easy-crop`; o que sai daqui é o `Enquadramento` do
 * projeto, em porcentagem da foto, lido do que a biblioteca mostra — a fonte da
 * verdade é a tela, e o servidor monta exatamente isso.
 *
 * A caixa tem a proporção da célula e a biblioteca recorta a caixa inteira, com
 * a foto "contida" nela: zoom 1 é a foto inteira (encaixar), abaixo disso sobra
 * margem, acima corta. `restrictPosition` desligado é o que deixa a foto sair
 * da caixa e a área passar de 0–100 %.
 */
export function EditorDeEnquadramento({
  fotoUrl,
  foto,
  celula,
  areaUtil,
  valor,
  ia,
  aoSalvar,
  aoCancelar,
}: {
  fotoUrl: string;
  /** Tamanho da foto: só a proporção importa. */
  foto: Tamanho;
  /** Tamanho da célula, em qualquer unidade: só a proporção importa. */
  celula: Tamanho;
  /**
   * O que do espaço o papel imprime e mantém (a área útil, em frações do espaço), ou nulo quando o espaço cabe
   * inteiro nela. O resto, que cai no picotado, aparece escurecido, com a linha da área por cima.
   */
  areaUtil?: AreaNaCelula | null;
  valor: Enquadramento;
  /** Sem isto, o editor não oferece os botões de IA. */
  ia?: IaDoEditor;
  aoSalvar: (enquadramento: Enquadramento) => void;
  aoCancelar: () => void;
}) {
  const proporcao = celula.largura / celula.altura;
  const fotoProporcao = foto.largura / foto.altura;
  // O zoom em que a foto cobre a caixa inteira (preencher).
  const zoomDePreencher = Math.max(fotoProporcao / proporcao, proporcao / fotoProporcao);
  // Os limites do pedido ao servidor (10 a 2000 % de largura) viram limites de zoom.
  const zoomMinimo = Math.max(0.3, zoomDePreencher / 19);
  const zoomMaximo = zoomDePreencher * 8;

  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [atual, setAtual] = useState<Enquadramento>(valor);
  const medida = useRef<{ midia: MediaSize | null; caixa: Size | null }>({
    midia: null,
    caixa: null,
  });

  // O tamanho da caixa em pixels, para dizer à biblioteca que a área de recorte
  // é a caixa inteira. Sem isso ela recorta a maior área da proporção que cabe
  // dentro da foto — menor que a célula, e escurece o resto.
  const caixaRef = useRef<HTMLDivElement>(null);
  const [caixa, setCaixa] = useState<Size | null>(null);
  useEffect(() => {
    const el = caixaRef.current;
    if (!el) return;
    const medir = () => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) setCaixa({ width: r.width, height: r.height });
    };
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, []);

  /** Põe a foto num enquadramento: converte a área em posição e zoom da biblioteca. */
  function aplicar(e: Enquadramento) {
    const { midia, caixa } = medida.current;
    if (!midia || !caixa) return;
    const area: Area = {
      x: e.x,
      y: e.y,
      width: e.largura,
      height: e.largura * (foto.largura / foto.altura) * (celula.altura / celula.largura),
    };
    const novo = getInitialCropFromCroppedAreaPercentages(
      area,
      midia,
      0,
      caixa,
      zoomMinimo,
      zoomMaximo,
    );
    setCrop(novo.crop);
    setZoom(novo.zoom);
  }

  // Escala logarítmica na barra: dobrar o zoom tem o mesmo peso em qualquer ponto.
  const passos = 1000;
  const logMin = Math.log(zoomMinimo);
  const logMax = Math.log(zoomMaximo);
  const naBarra = Math.round(((Math.log(zoom) - logMin) / (logMax - logMin)) * passos);

  return (
    <div className="flex flex-col gap-4 p-4">
      <div
        ref={caixaRef}
        className="relative mx-auto touch-none overflow-hidden rounded-peca bg-white"
        style={{
          aspectRatio: String(proporcao),
          width: `min(100%, calc(55dvh * ${proporcao}))`,
        }}
      >
        {caixa && (
          <Cropper
            image={fotoUrl}
            cropSize={caixa}
            crop={crop}
            zoom={zoom}
            minZoom={zoomMinimo}
            maxZoom={zoomMaximo}
            aspect={proporcao}
            objectFit="contain"
            restrictPosition={false}
            showGrid={false}
            zoomSpeed={0.6}
            initialCroppedAreaPercentages={{
              x: valor.x,
              y: valor.y,
              width: valor.largura,
              height:
                valor.largura * (foto.largura / foto.altura) * (celula.altura / celula.largura),
            }}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onMediaLoaded={(m) => (medida.current.midia = m)}
            onCropSizeChange={(c) => (medida.current.caixa = c)}
            onCropAreaChange={(area) =>
              setAtual({
                x: arredondar(area.x),
                y: arredondar(area.y),
                largura: arredondar(area.width),
              })
            }
            style={{ cropAreaStyle: { border: "1px solid rgba(0,0,0,0.25)" } }}
          />
        )}
        {areaUtil && (
          // A mesma linha da prévia da folha. A sombra enorme escurece o que está fora dela — o que cai no
          // picotado —, e o `overflow-hidden` da caixa a corta nas bordas. Não captura o gesto: arrastar e
          // dar zoom passam por cima.
          <span
            aria-hidden
            data-area-util
            className="pointer-events-none absolute z-10 border border-dashed border-sky-400"
            style={{
              left: `${areaUtil.x * 100}%`,
              top: `${areaUtil.y * 100}%`,
              width: `${areaUtil.largura * 100}%`,
              height: `${areaUtil.altura * 100}%`,
              boxShadow: "0 0 0 100vmax rgb(0 0 0 / 0.34)",
            }}
          />
        )}
      </div>

      {areaUtil && (
        <p className="flex items-start gap-2 text-[0.8125rem] leading-relaxed text-texto-suave">
          <span
            aria-hidden
            className="mt-1 inline-block h-3 w-5 shrink-0 border border-dashed border-sky-400"
          />
          <span>
            A linha azul tracejada é a área que o papel imprime ({emMm(AREA_UTIL.larguraMm)} ×{" "}
            {emMm(AREA_UTIL.alturaMm)} mm). O que fica fora dela, na parte escurecida, cai no
            picotado e se perde.
          </span>
        </p>
      )}

      <label className="flex items-center gap-3 text-[0.8125rem] text-texto-suave">
        <span className="w-12 shrink-0">Zoom</span>
        <input
          type="range"
          min={0}
          max={passos}
          value={Math.min(Math.max(naBarra, 0), passos)}
          onChange={(e) =>
            setZoom(Math.exp(logMin + (Number(e.target.value) / passos) * (logMax - logMin)))
          }
          className="h-8 min-w-0 flex-1 accent-[var(--color-brasa)]"
        />
      </label>

      <div className="flex flex-wrap gap-2">
        <Botao
          tom="quieto"
          onClick={() => aplicar(enquadramentoInicial(foto, celula, "preencher"))}
        >
          Preencher
        </Botao>
        <Botao tom="quieto" onClick={() => aplicar(enquadramentoInicial(foto, celula, "encaixar"))}>
          Encaixar
        </Botao>
      </div>
      {ia && <SecaoDeIa ia={ia} />}

      <p className="text-[0.8125rem] leading-relaxed text-texto-fraco">
        Preencher cobre o espaço e corta o que sobra. Encaixar mostra a foto inteira e deixa margem
        branca. Arraste para reposicionar.
      </p>

      <div className="flex flex-wrap justify-end gap-2 border-t border-borda pt-4">
        <Botao tom="quieto" onClick={aoCancelar}>
          Cancelar
        </Botao>
        <Botao tom="brasa" onClick={() => aoSalvar(atual)}>
          Usar este enquadramento
        </Botao>
      </div>
    </div>
  );
}

/**
 * Os botões de IA. Cada um consome 1 foto do pacote, então pede confirmação antes,
 * dizendo quantas restam.
 */
function SecaoDeIa({ ia }: { ia: IaDoEditor }) {
  const [confirmando, setConfirmando] = useState<"ajustar" | "ampliar" | null>(null);
  const semSaldo = ia.restantes === 0;
  const ocupado = ia.ocupado !== null;

  return (
    <section aria-label="Com IA" className="flex flex-col gap-3 border-t border-borda pt-4">
      <div>
        <h3 className="text-[0.9375rem] font-medium text-texto">Com IA</h3>
        <p className="mt-1 text-[0.8125rem] leading-relaxed text-texto-fraco">
          Ajustar reposiciona a foto para manter todas as pessoas inteiras. Ampliar completa o
          cenário ao redor para a foto caber no espaço, sem mexer nas pessoas.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Botao
          tom="quieto"
          disabled={ocupado || semSaldo || ia.ampliada}
          carregando={ia.ocupado === "ajustar"}
          onClick={() => setConfirmando("ajustar")}
          title={ia.ampliada ? "Desfaça a ampliação para ajustar" : undefined}
        >
          Ajustar com IA
        </Botao>
        <Botao
          tom="quieto"
          disabled={ocupado || semSaldo}
          carregando={ia.ocupado === "ampliar"}
          onClick={() => setConfirmando("ampliar")}
        >
          {ia.ampliada ? "Ampliar de novo" : "Ampliar a cena com IA"}
        </Botao>
        {ia.ampliada && (
          // "Foto original" é uma versão da foto (a que o operador tirou, #94); aqui é só tirar a ampliação.
          <Botao tom="fantasma" formato="nu" disabled={ocupado} onClick={ia.aoVoltarAOriginal}>
            Desfazer a ampliação
          </Botao>
        )}
      </div>

      {confirmando !== null && !ocupado && (
        <div className="rounded-peca border border-borda bg-superficie p-3">
          <p className="text-[0.875rem] text-texto">
            Consome 1 foto do pacote
            {typeof ia.restantes === "number" ? ` (restam ${ia.restantes})` : ""}. Continuar?
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Botao
              tom="brasa"
              onClick={() => {
                const qual = confirmando;
                setConfirmando(null);
                if (qual === "ajustar") ia.aoAjustar();
                else ia.aoAmpliar();
              }}
            >
              Continuar
            </Botao>
            <Botao tom="quieto" onClick={() => setConfirmando(null)}>
              Cancelar
            </Botao>
          </div>
        </div>
      )}

      {semSaldo && (
        <p className="text-[0.8125rem] text-texto-suave">
          O pacote não tem mais fotos para usar a IA.
        </p>
      )}

      {ia.ocupado !== null && (
        <p role="status" className="flex items-center gap-2 text-[0.8125rem] text-texto-suave">
          <Girando className="size-4" />
          {ia.ocupado === "ampliar"
            ? "Ampliando a cena… leva cerca de 40 segundos. Pode fechar esta tela: o resultado entra na folha."
            : "Procurando as pessoas na foto…"}
        </p>
      )}

      {ia.mensagem && !ocupado && (
        <p role="status" className="text-[0.8125rem] leading-relaxed text-texto-suave">
          {ia.mensagem}
        </p>
      )}
    </section>
  );
}
